import React from 'react';
import { ArrowRightOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

interface ClientCardProps {
  email: string;
  plan: string;
  status: string;
  mrr: number;
  balance: number;
  serviceCount: number;
  onViewDetails?: () => void;
}

const ClientCard: React.FC<ClientCardProps> = ({
  email,
  plan,
  status,
  mrr,
  balance,
  serviceCount,
  onViewDetails,
}) => {
  const statusConfig: Record<string, { badge: string; icon: React.ReactNode; label: string }> = {
    ACTIVE: {
      badge: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
      icon: <CheckCircleOutlined className="text-lg" />,
      label: 'Activo',
    },
    SUSPENDED: {
      badge: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400',
      icon: <ExclamationCircleOutlined className="text-lg" />,
      label: 'Suspendido',
    },
    INACTIVE: {
      badge: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
      icon: <ExclamationCircleOutlined className="text-lg" />,
      label: 'Inactivo',
    },
  };

  const normalizedStatus = (status || '').toUpperCase();
  const config = statusConfig[normalizedStatus] ?? {
    badge: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    icon: null,
    label: status || 'Desconocido',
  };
  const isNegativeBalance = balance < 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-gray-200 dark:border-gray-700 p-6">
      {/* Header with email and status */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">
            {email}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Plan: <span className="font-medium text-gray-900 dark:text-white">{plan}</span>
          </p>
        </div>
        <div className={`ml-3 flex-shrink-0 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${config.badge}`}>
          {config.icon}
          <span>{config.label}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {/* MRR */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">MRR</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
            ${mrr.toFixed(2)}
          </p>
        </div>

        {/* Balance */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Balance</p>
          <p className={`text-xl font-bold mt-1 ${isNegativeBalance ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
            ${balance.toFixed(2)}
          </p>
        </div>

        {/* Services */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Services</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
            {serviceCount}
          </p>
        </div>
      </div>

      {/* Action Button */}
      <button
        onClick={onViewDetails}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
      >
        <span>View Details</span>
        <ArrowRightOutlined className="text-lg" />
      </button>
    </div>
  );
};

export default ClientCard;
